import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MapPinned, Plus, Share2, MapPin } from "lucide-react";

export default function Trips() {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    api.get("/trips").then(r => setTrips(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/trips", { name, description, venue_ids: [], is_public: false });
      toast.success("Trip created");
      setOpen(false);
      setName(""); setDescription("");
      navigate(`/trips/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create trip");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-blue-600 text-white flex items-center justify-center"><MapPinned className="w-6 h-6"/></div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-gray-900">Trip lists</h1>
          </div>
          <p className="mt-3 text-gray-700 max-w-2xl">Group saved places into named trips — perfect for a family day out or a shareable outing plan.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-blue-600 hover:bg-blue-700 h-11 px-5" data-testid="new-trip-btn"><Plus className="w-4 h-4 mr-1"/>New trip</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New trip</DialogTitle>
              <DialogDescription>Give your trip a name — you can add places to it next.</DialogDescription>
            </DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div>
                <Label htmlFor="tname">Name</Label>
                <Input id="tname" required value={name} onChange={e => setName(e.target.value)} placeholder="Saturday in Salisbury"
                  className="mt-1 border-2 border-gray-300 focus:border-blue-600" data-testid="trip-name-input"/>
              </div>
              <div>
                <Label htmlFor="tdesc">Notes (optional)</Label>
                <Textarea id="tdesc" rows={3} value={description} onChange={e => setDescription(e.target.value)}
                  className="mt-1 border-2 border-gray-300 focus:border-blue-600"/>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={busy} className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="trip-create-submit">
                  {busy ? "Creating…" : "Create trip"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="mt-10 text-gray-500">Loading…</div>
      ) : trips.length === 0 ? (
        <div className="mt-10 border-2 border-dashed border-gray-300 rounded-xl p-12 text-center">
          <p className="text-lg text-gray-700">You haven't created any trips yet.</p>
          <Button className="mt-4 rounded-full bg-blue-600 hover:bg-blue-700" onClick={() => setOpen(true)}>Create your first trip</Button>
        </div>
      ) : (
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {trips.map(t => (
            <Link key={t.id} to={`/trips/${t.id}`} data-testid={`trip-card-${t.id}`}>
              <Card className="bg-white border border-gray-200 hover:border-gray-900 transition-colors duration-200 rounded-xl h-full">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2">
                    {t.is_public ? (
                      <Badge className="rounded-full bg-emerald-100 text-emerald-900 border border-emerald-200"><Share2 className="w-3 h-3 mr-1"/>Public</Badge>
                    ) : (
                      <Badge variant="outline" className="rounded-full">Private</Badge>
                    )}
                    <span className="text-xs text-gray-500">Updated {new Date(t.updated_at).toLocaleDateString()}</span>
                  </div>
                  <h3 className="mt-3 font-heading text-xl font-semibold text-gray-900">{t.name}</h3>
                  {t.description && <p className="mt-1 text-sm text-gray-700 line-clamp-2">{t.description}</p>}
                  <div className="mt-4 text-sm text-gray-600 flex items-center gap-1"><MapPin className="w-4 h-4"/>{(t.venues || []).length} place{(t.venues || []).length === 1 ? "" : "s"}</div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
